import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { DayGridModel, CourtColumnModel } from '../../court-grid.model';
import { CourtColumnComponent, FreeCellActivatedEvent, FreeCellDragStartEvent } from '../court-column/court-column.component';
import { AggregatedCourseSessionDto } from '../../../../../service/court-availability-service';
import { parseLocalDate } from '../../../../../shared/utils/date-format.util';

/**
 * Dumb: one day's header (weekday/date + court name sub-row) plus its
 * visible court columns. Court visibility/order is entirely driven by
 * `visibleCourtIds` (courtId-keyed, not index-keyed) so root-level paging
 * never reparents column DOM nodes.
 */
@Component({
  selector: 'app-court-day-group',
  standalone: true,
  imports: [CourtColumnComponent],
  templateUrl: './court-day-group.component.html',
  styleUrls: ['./court-day-group.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourtDayGroupComponent {
  day = input.required<DayGridModel>();
  visibleCourtIds = input<string[]>([]);
  pxPerMinute = input.required<number>();
  intervalMinutes = input.required<number>();
  courtColumnWidth = input<number>(90);
  maxBookableUtc = input<Date | null>(null);

  freeCellActivated = output<FreeCellActivatedEvent>();
  /** Pass-through from app-court-column — mouse press-drag-release create gesture. */
  freeCellDragStarted = output<FreeCellDragStartEvent>();
  /** Pass-through from app-court-column — course block activated (see CourtColumnComponent). */
  courseSessionClicked = output<AggregatedCourseSessionDto>();

  visibleCourts = computed<CourtColumnModel[]>(() => {
    const ids = this.visibleCourtIds();
    const byId = new Map(this.day().courts.map(c => [c.courtId, c]));
    return ids.map(id => byId.get(id)).filter((c): c is CourtColumnModel => !!c);
  });

  totalWidthPx = computed(() => this.visibleCourts().length * this.courtColumnWidth());

  dayNameLabel = computed(() => {
    const date = parseLocalDate(this.day().date);
    return date ? date.toLocaleDateString('de-DE', { weekday: 'short' }) : '';
  });

  dayDateLabel = computed(() => {
    const date = parseLocalDate(this.day().date);
    if (!date) return '';
    // Manual DD.MM — de-DE's toLocaleDateString appends its own trailing
    // dot ("12.07."), which produced "12.07.." when a dot was concatenated.
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}`;
  });

  onFreeCellActivated(event: FreeCellActivatedEvent): void {
    this.freeCellActivated.emit(event);
  }

  onFreeCellDragStarted(event: FreeCellDragStartEvent): void {
    this.freeCellDragStarted.emit(event);
  }

  onCourseSessionClicked(session: AggregatedCourseSessionDto): void {
    this.courseSessionClicked.emit(session);
  }
}
